/***
*
*   INBOX
*   Intercom conversation queue with quick-access diagnostics
*
**********/

import { useState, useCallback } from 'react';
import { Animate, useAPI } from 'components/lib';
import { useNavigate } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Compact action bar
// ---------------------------------------------------------------------------

function QuickSearch({ placeholder, basePath }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  function handleSubmit(e) {
    e.preventDefault();
    const q = query.trim();
    if (q) navigate(`${basePath}?search=${encodeURIComponent(q)}`);
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 6, flex: 1 }}>
      <div style={{ position: 'relative', flex: 1 }}>
        <span style={{
          position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)',
          color: '#9ca3af', pointerEvents: 'none', display: 'flex',
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
        </span>
        <input
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={e => setQuery(e.target.value)}
          style={{
            width: '100%',
            paddingLeft: 30,
            paddingRight: 10,
            height: 34,
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            fontSize: 13,
            color: '#374151',
            outline: 'none',
            boxSizing: 'border-box',
            background: '#fff',
          }}
        />
      </div>
      <button type="submit" style={{
        padding: '0 14px', height: 34,
        background: '#2563eb', color: '#fff',
        border: 'none', borderRadius: 8,
        fontSize: 13, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
      }}>
        Go
      </button>
    </form>
  );
}

function ActionBar() {
  const navigate = useNavigate();
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 10,
      padding: '10px 16px',
      marginBottom: 20,
    }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
        Quick search
      </span>
      <div style={{ width: 1, height: 20, background: '#e5e7eb' }} />
      <QuickSearch placeholder="User by email or name…" basePath="/users" />
      <div style={{ width: 1, height: 20, background: '#e5e7eb' }} />
      <QuickSearch placeholder="Account by name…" basePath="/accounts" />
      <div style={{ width: 1, height: 20, background: '#e5e7eb' }} />
      <button
        onClick={() => navigate('/logs?tab=errors')}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '0 14px', height: 34,
          background: '#fff', color: '#374151',
          border: '1px solid #e5e7eb', borderRadius: 8,
          fontSize: 13, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        Errors
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Intercom conversation list
// ---------------------------------------------------------------------------

const STATE_COLOURS = {
  open:    { bg: '#fef3c7', color: '#92400e', label: 'Open' },
  snoozed: { bg: '#ede9fe', color: '#5b21b6', label: 'Snoozed' },
  closed:  { bg: '#f0fdf4', color: '#166534', label: 'Closed' },
};

function StateBadge({ state }) {
  const s = STATE_COLOURS[state] || { bg: '#f3f4f6', color: '#374151', label: state };
  return (
    <span style={{
      display: 'inline-block', background: s.bg, color: s.color,
      fontSize: 11, fontWeight: 600, borderRadius: 4, padding: '2px 7px', whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
}

function formatRelative(ts) {
  if (!ts) return '—';
  const diff = Date.now() - ts * 1000;
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins < 2)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

const STATE_TABS = ['open', 'snoozed', 'closed', 'all'];

function ConversationRow({ conv }) {
  return (
    <a
      href={conv.conversation_url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 180px 100px 80px',
        gap: 12,
        alignItems: 'center',
        padding: '11px 0',
        borderBottom: '1px solid #f3f4f6',
        textDecoration: 'none',
        color: 'inherit',
      }}
      onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
      onMouseLeave={e => e.currentTarget.style.background = 'none'}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {conv.title}
        </div>
        {(conv.contact?.name || conv.contact?.email) && (
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
            {conv.contact.name}{conv.contact.name && conv.contact.email ? ' · ' : ''}{conv.contact.email}
          </div>
        )}
      </div>
      <div style={{ fontSize: 13, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {conv.assignee || <span style={{ color: '#d1d5db' }}>Unassigned</span>}
      </div>
      <div><StateBadge state={conv.state} /></div>
      <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'right' }}>
        {formatRelative(conv.updated_at)}
      </div>
    </a>
  );
}

function ConversationTable() {
  const [activeState, setActiveState] = useState('open');
  const [page, setPage] = useState(1);

  const url = `/api/intercom/conversations?state=${activeState}&page=${page}&per_page=15`;
  const { data, loading, error } = useAPI(url);

  const conversations = data?.conversations || [];
  const totalCount    = data?.total_count ?? 0;
  const totalPages    = data?.pages?.total_pages ?? 1;

  const handleStateChange = useCallback((s) => { setActiveState(s); setPage(1); }, []);

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>

      {/* Tabs + open in Intercom */}
      <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #e5e7eb', padding: '0 20px' }}>
        {STATE_TABS.map(s => (
          <button
            key={s}
            onClick={() => handleStateChange(s)}
            style={{
              padding: '12px 14px',
              fontSize: 13,
              fontWeight: activeState === s ? 600 : 400,
              color: activeState === s ? '#1974f9' : '#6b7280',
              background: 'none', border: 'none',
              borderBottom: activeState === s ? '2px solid #1974f9' : '2px solid transparent',
              cursor: 'pointer', marginBottom: -1, textTransform: 'capitalize',
            }}
          >
            {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          {!loading && (
            <span style={{ fontSize: 12, color: '#9ca3af' }}>{totalCount} total</span>
          )}
          <a
            href="https://app.intercom.com/a/apps/hjp71o6u/inbox"
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: '#2563eb', textDecoration: 'none', fontWeight: 500 }}
          >
            Open in Intercom
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
          </a>
        </div>
      </div>

      {/* Column headers */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 180px 100px 80px',
        gap: 12, padding: '8px 20px',
        background: '#f9fafb', borderBottom: '1px solid #e5e7eb',
      }}>
        {['Subject', 'Assignee', 'State', 'Updated'].map(h => (
          <div key={h} style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: h === 'Updated' ? 'right' : undefined }}>
            {h}
          </div>
        ))}
      </div>

      {/* Body */}
      <div style={{ padding: '0 20px', minHeight: 80 }}>
        {loading && <div style={{ padding: '40px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Loading conversations…</div>}
        {error && <div style={{ padding: '40px 0', textAlign: 'center', color: '#dc2626', fontSize: 13 }}>Failed to load. Check server logs.</div>}
        {!loading && !error && conversations.length === 0 && (
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
            No {activeState === 'all' ? '' : activeState + ' '}conversations.
          </div>
        )}
        {!loading && conversations.map(conv => <ConversationRow key={conv.id} conv={conv} />)}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, padding: '12px 20px', borderTop: '1px solid #f3f4f6' }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            style={{ padding: '4px 12px', fontSize: 13, borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', color: page <= 1 ? '#d1d5db' : '#374151', cursor: page <= 1 ? 'default' : 'pointer' }}
          >‹ Prev</button>
          <span style={{ fontSize: 13, color: '#6b7280' }}>Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            style={{ padding: '4px 12px', fontSize: 13, borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', color: page >= totalPages ? '#d1d5db' : '#374151', cursor: page >= totalPages ? 'default' : 'pointer' }}
          >Next ›</button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export function SupportTools() {
  return (
    <Animate type="pop">
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 }}>Inbox</h1>
        <p style={{ fontSize: 14, color: '#6b7280', margin: '4px 0 0' }}>
          Open support conversations — search users or accounts to investigate a ticket.
        </p>
      </div>

      <ActionBar />
      <ConversationTable />
    </Animate>
  );
}
